import { mergeSlotProps } from '@mui/material/utils';
import {
  type DateRange,
  dateRangeCalendarClasses,
  StaticDateRangePicker,
  type StaticDateRangePickerProps,
} from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { asUTCDate, parseDate } from 'shared/utils/DateTime';

type LuxonStaticDateRangePickerProps = Omit<StaticDateRangePickerProps, 'onChange'> & {
  onChange?: (range: DateRange<DateTime>) => void;
};

type CalendarHeaderSlotProps = NonNullable<NonNullable<StaticDateRangePickerProps['slotProps']>['calendarHeader']>;
type NavigationButtonSlotProps = NonNullable<
  NonNullable<StaticDateRangePickerProps['slotProps']>['previousIconButton']
>;
type NavigationIconSlotProps = NonNullable<NonNullable<StaticDateRangePickerProps['slotProps']>['leftArrowIcon']>;

const V5_CALENDAR_HEADER_SLOT_PROPS: CalendarHeaderSlotProps = {
  sx: {
    justifyContent: 'space-between',
    marginBottom: 0,
    marginTop: 0,
    maxHeight: 'none',
    minHeight: '0px',
    padding: '16px 16px 8px',
  },
};
const V5_NAVIGATION_BUTTON_SLOT_PROPS: NavigationButtonSlotProps = { size: 'small' };
const V5_NAVIGATION_ICON_SLOT_PROPS: NavigationIconSlotProps = { fontSize: 'medium' };

export default function LuxonStaticDateRangePicker({
  onChange,
  slotProps,
  sx,
  value,
  ...props
}: LuxonStaticDateRangePickerProps) {
  const parsedValue: DateRange<DateTime> | undefined =
    value === undefined ? undefined : [parseDate(value[0]), parseDate(value[1])];

  return (
    <StaticDateRangePicker
      displayStaticWrapperAs="desktop"
      onChange={(range) => {
        const [start, end] = range;

        onChange?.([asUTCDate(start), asUTCDate(end)?.endOf('day')] as DateRange<DateTime>);
      }}
      value={parsedValue}
      {...props}
      slotProps={{
        ...slotProps,
        calendarHeader: mergeSlotProps<CalendarHeaderSlotProps, CalendarHeaderSlotProps>(
          slotProps?.calendarHeader ?? {},
          V5_CALENDAR_HEADER_SLOT_PROPS
        ),
        leftArrowIcon: mergeSlotProps<NavigationIconSlotProps, NavigationIconSlotProps>(
          slotProps?.leftArrowIcon ?? {},
          V5_NAVIGATION_ICON_SLOT_PROPS
        ),
        nextIconButton: mergeSlotProps<NavigationButtonSlotProps, NavigationButtonSlotProps>(
          slotProps?.nextIconButton ?? {},
          V5_NAVIGATION_BUTTON_SLOT_PROPS
        ),
        previousIconButton: mergeSlotProps<NavigationButtonSlotProps, NavigationButtonSlotProps>(
          slotProps?.previousIconButton ?? {},
          V5_NAVIGATION_BUTTON_SLOT_PROPS
        ),
        rightArrowIcon: mergeSlotProps<NavigationIconSlotProps, NavigationIconSlotProps>(
          slotProps?.rightArrowIcon ?? {},
          V5_NAVIGATION_ICON_SLOT_PROPS
        ),
      }}
      sx={[
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        { [`& .${dateRangeCalendarClasses.monthContainer}:not(:last-of-type)`]: { borderRightWidth: '2px' } },
      ]}
    />
  );
}
